import React, { useState, useEffect } from "react";
import { FaSearch, FaFilter, FaEye, FaDownload, FaSync, FaSort, FaShoppingCart } from "react-icons/fa";
import api from "../../utilis/api";
import { toast } from "react-toastify";

const ITEMS_PER_PAGE = 10;

const Orders = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);

  const orderStatuses = [
    { value: 'all', label: 'All Orders', color: 'bg-gray-500' },
    { value: 'Processing', label: 'Processing', color: 'bg-yellow-500' },
    { value: 'Shipped', label: 'Shipped', color: 'bg-blue-500' },
    { value: 'Delivered', label: 'Delivered', color: 'bg-green-500' },
    { value: 'Cancelled', label: 'Cancelled', color: 'bg-red-500' }
  ];

  const paymentStatuses = [
    { value: 'Pending', label: 'Pending', color: 'bg-yellow-500' },
    { value: 'Paid', label: 'Paid', color: 'bg-green-500' },
    { value: 'Failed', label: 'Failed', color: 'bg-red-500' }
  ];

  useEffect(() => {
    fetchOrders();
  }, [currentPage, filters]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        ...(filters.status !== 'all' && { status: filters.status }),
        ...(filters.search && { search: filters.search })
      });

      const response = await api.get(`/admin/orders?${params}`);
      
      if (response.data?.success) {
        setOrders(response.data.data.orders);
        setTotalOrders(response.data.data.pagination.totalOrders);
        setTotalPages(response.data.data.pagination.totalPages);
      } else {
        throw new Error("Failed to fetch orders");
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to load orders");
      
      // Don't show mock data, show empty state instead
      setOrders([]);
      setTotalOrders(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus, type = 'order') => {
    try {
      const updateData = type === 'order' 
        ? { orderStatus: newStatus }
        : { paymentStatus: newStatus };

      const response = await api.put(`/admin/orders/${orderId}/status`, updateData);
      
      if (response.data?.success) {
        toast.success(`${type === 'order' ? 'Order' : 'Payment'} status updated successfully`);
        fetchOrders(); // Refresh the orders list
      } else {
        throw new Error("Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(`Failed to update ${type} status`);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    // fetchOrders will be called automatically due to useEffect dependency on filters
  };

  const getStatusColor = (status, type = 'order') => {
    const statuses = type === 'order' ? orderStatuses : paymentStatuses;
    const statusObj = statuses.find(s => s.value === status);
    return statusObj?.color || 'bg-gray-500';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const OrderModal = ({ order, onClose }) => {
    if (!order) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-[#121218] rounded-xl border border-white/10 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">Order Details {order.orderId || `#${order.orderNo}`}</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Order Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Order Information</h3>
                <div className="space-y-2">
                  <p className="text-gray-400">Order ID: <span className="text-white">{order.orderId || `#${order.orderNo}`}</span></p>
                  <p className="text-gray-400">Date: <span className="text-white">{formatDate(order.createdAt)}</span></p>
                  <p className="text-gray-400">Total: <span className="text-white">${order.totalPrice}</span></p>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400">Status:</span>
                    <select
                      value={order.orderStatus}
                      onChange={(e) => handleStatusUpdate(order._id, e.target.value, 'order')}
                      className="bg-[#1a1a1f] border border-white/10 rounded px-2 py-1 text-white text-sm"
                    >
                      {orderStatuses.slice(1).map(status => (
                        <option key={status.value} value={status.value}>{status.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400">Payment:</span>
                    <select
                      value={order.paymentStatus}
                      onChange={(e) => handleStatusUpdate(order._id, e.target.value, 'payment')}
                      className="bg-[#1a1a1f] border border-white/10 rounded px-2 py-1 text-white text-sm"
                    >
                      {paymentStatuses.map(status => (
                        <option key={status.value} value={status.value}>{status.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Customer Information</h3>
                <div className="space-y-2">
                  <p className="text-gray-400">Name: <span className="text-white">{order.user?.name || order.shippingAddress?.name}</span></p>
                  <p className="text-gray-400">Email: <span className="text-white">{order.user?.email || order.shippingAddress?.email}</span></p>
                  {order.shippingAddress && (
                    <div>
                      <p className="text-gray-400 mb-1">Shipping Address:</p>
                      <div className="text-white text-sm bg-white/5 p-3 rounded">
                        <p>{order.shippingAddress.address}</p>
                        <p>{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.postalCode}</p>
                        <p>{order.shippingAddress.country}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Order Items */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Order Items</h3>
              <div className="bg-white/5 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-white/10">
                    <tr>
                      <th className="text-left p-3 text-gray-400">Product</th>
                      <th className="text-left p-3 text-gray-400">Quantity</th>
                      <th className="text-left p-3 text-gray-400">Price</th>
                      <th className="text-left p-3 text-gray-400">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.orderItems?.map((item, index) => (
                      <tr key={index} className="border-t border-white/10">
                        <td className="p-3 text-white">{item.product?.name || 'Product'}</td>
                        <td className="p-3 text-white">{item.quantity}</td>
                        <td className="p-3 text-white">${item.price}</td>
                        <td className="p-3 text-white">${(item.quantity * item.price).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6 min-h-screen bg-[#0a0a0f]">
      {/* Header */}
      <div className="bg-[#121218] p-6 border border-white/10 rounded-xl">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2 bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
              Order Management
            </h1>
            <p className="text-gray-400">
              {loading ? "Loading orders..." : (
                filters.search ? 
                  `Found ${totalOrders} orders matching "${filters.search}"` :
                filters.status !== 'all' ?
                  `Found ${totalOrders} ${filters.status.toLowerCase()} orders` :
                  `Total ${totalOrders} orders found`
              )}
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={fetchOrders}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <FaSync className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            {(filters.search || filters.status !== 'all') && (
              <button
                onClick={() => {
                  setFilters({ status: 'all', search: '', sortBy: 'createdAt', sortOrder: 'desc' });
                  setCurrentPage(1);
                }}
                className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#121218] p-6 border border-white/10 rounded-xl">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by order ID, customer name, or email..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 bg-[#1a1a1f] border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-amber-500"
              />
            </div>
          </form>
          
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <FaFilter className="text-gray-400" />
            <select
              value={filters.status}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, status: e.target.value }));
                setCurrentPage(1);
              }}
              className="bg-[#1a1a1f] border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-amber-500"
            >
              {orderStatuses.map(status => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-[#121218] border border-white/10 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4"></div>
            <p className="text-gray-400">Loading orders...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">
                    <div className="flex items-center gap-2">
                      Order ID <FaSort className="text-xs" />
                    </div>
                  </th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">Customer</th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">Total</th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">Payment</th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">Status</th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">Date</th>
                  <th className="text-left py-4 px-6 text-gray-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.length > 0 ? (
                  orders.map((order) => (
                    <tr key={order._id} className="border-t border-white/5 hover:bg-white/5 transition-colors">
                      <td className="py-4 px-6">
                        <span className="text-white font-medium">{order.orderId || `#${order.orderNo}`}</span>
                      </td>
                      <td className="py-4 px-6">
                        <div>
                          <p className="text-white font-medium">{order.user?.name || order.shippingAddress?.name}</p>
                          <p className="text-gray-400 text-sm">{order.user?.email || order.shippingAddress?.email}</p>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="text-white font-medium">${order.totalPrice}</span>
                      </td>
                      <td className="py-4 px-6">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(order.paymentStatus, 'payment')}`}>
                          {order.paymentStatus}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(order.orderStatus)}`}>
                          {order.orderStatus}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <span className="text-gray-400 text-sm">{formatDate(order.createdAt)}</span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowOrderModal(true);
                            }}
                            className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                            title="View Details"
                          >
                            <FaEye className="text-sm" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-gray-400">
                      <div className="flex flex-col items-center">
                        <FaShoppingCart className="text-4xl mb-4 opacity-50" />
                        {filters.search ? (
                          <div>
                            <p className="text-lg mb-2">No orders found for "{filters.search}"</p>
                            <p className="text-sm">Try adjusting your search terms or filters</p>
                          </div>
                        ) : filters.status !== 'all' ? (
                          <div>
                            <p className="text-lg mb-2">No {filters.status.toLowerCase()} orders found</p>
                            <p className="text-sm">Try selecting a different status filter</p>
                          </div>
                        ) : (
                          <div>
                            <p className="text-lg mb-2">No orders found</p>
                            <p className="text-sm">Orders will appear here once customers start placing them</p>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-6 border-t border-white/10">
            <div className="flex items-center justify-between">
              <p className="text-gray-400 text-sm">
                Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, totalOrders)} of {totalOrders} orders
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 bg-[#1a1a1f] border border-white/10 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10 transition-colors"
                >
                  Previous
                </button>
                
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 rounded transition-colors ${
                        currentPage === page
                          ? 'bg-amber-600 text-white'
                          : 'bg-[#1a1a1f] border border-white/10 text-white hover:bg-white/10'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 bg-[#1a1a1f] border border-white/10 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Order Modal */}
      {showOrderModal && (
        <OrderModal
          order={selectedOrder}
          onClose={() => {
            setShowOrderModal(false);
            setSelectedOrder(null);
          }}
        />
      )}
    </div>
  );
};

export default Orders;
